import React from "react";
import { useSelector } from "react-redux";
import { InputAdornment, TextField, Typography } from "@material-ui/core";
import useRemoveArrowsNumberInput from "../../util/useRemoveArrowsNumberInput";
import { FormattedMessage } from "react-intl";
import { getLocaleFromUser } from "../../util";

const LocaleNumberField = ({
  defaultValue = 0,
  decimalPlaces = 2,
  disabled,
  endAdornment,
  fullWidth,
  label = "app.percentage.title",
  max,
  min,
  value,
  setValue,
  checkEmptyError = true,
  onKeyUp,
  maxLength = 16,
}) => {
  const currentUser = useSelector((state) => state.user.user.me);
  const classes = useRemoveArrowsNumberInput();

  const getValueByLocale = React.useCallback(
    (rawValue) => {
      if (rawValue === "" || rawValue === null) return "";

      if (decimalPlaces === 0) {
        return String(rawValue);
      }

      const userLocale = getLocaleFromUser(currentUser.locale);
      return rawValue.toLocaleString(userLocale, { useGrouping: false, maximumFractionDigits: decimalPlaces });
    },
    [currentUser.locale, decimalPlaces],
  );
  const [displayValue, setDisplayValue] = React.useState(getValueByLocale(value ?? defaultValue));

  const handleValue = (e) => {
    let input = e.target.value;

    input = input.replace(/[^0-9.,]/g, "");
    if (input === "") {
      setValue("");
      setDisplayValue("");
      return;
    }

    let cleanedInput = input.replace(/^0+(\d)/, "$1");

    const dots = (cleanedInput.match(/\./g) || []).length;
    const commas = (cleanedInput.match(/,/g) || []).length;

    if (dots > 1 || commas > 1) return;

    const hasComma = cleanedInput.includes(",");
    const hasDot = cleanedInput.includes(".");
    if (hasComma && hasDot) return;

    if (decimalPlaces === 0) {
      if (hasComma || hasDot) return;
    }

    const parts = cleanedInput.split(/[,.]/);
    if (parts.length > 1 && parts[1].length > decimalPlaces) {
      return;
    }

    const normalizedInput = cleanedInput.replace(",", ".");
    const num = Number(normalizedInput);
    if (max && num > max) return;
    if (min && num < min) return;

    if (Number.isNaN(num)) return;

    setValue(num);
    setDisplayValue(cleanedInput);
  };

  React.useEffect(() => {
    if (value === "" || value === null || value === undefined) {
      setDisplayValue("");
      return;
    }

    setDisplayValue(getValueByLocale(value));
  }, [value, getValueByLocale]);

  const isValueEmpty = checkEmptyError && value === "";

  return (
    <>
      <TextField
        id="percentage"
        fullWidth={fullWidth}
        label={<FormattedMessage id={label} />}
        value={displayValue}
        onChange={handleValue}
        onKeyUp={onKeyUp}
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
        className={classes.input}
        variant="outlined"
        size="small"
        type="text"
        InputProps={{
          autoComplete: "off",
          endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : null,
        }}
        inputProps={{
          inputMode: "decimal",
          pattern: "[0-9]*",
          maxLength,
        }}
        error={isValueEmpty}
        helperText={
          isValueEmpty && (
            <Typography component={"span"} color="error" variant="caption">
              <FormattedMessage id="app.error.field.empty" />
            </Typography>
          )
        }
      />
    </>
  );
};

export default LocaleNumberField;
